import { Controller } from "@hotwired/stimulus"
import { editorStore, isoLocal } from "../editor/store"

// Compact always-visible hour slider shown above the mobile dock whenever
// the insolation mode is "shadows" or "heatmap". The full insolation panel
// (date, season shortcuts, UTC offset, altitude/azimuth readout) still lives
// in the bottom sheet — this bar is purely for quick time scrubbing, which
// was buried three taps deep in Stage 8a.
//
// Tapping "..." opens the full menu sheet (same event the dock uses);
// tapping "✕" turns insolation off.
export default class extends Controller {
  static targets = ["hourSlider", "hourLabel"]

  connect() {
    this._onSun = () => this.#syncSliderFromStore()
    this._onMode = () => this.#syncVisibility()
    editorStore.addEventListener("sun:change", this._onSun)
    editorStore.addEventListener("insolation:mode-change", this._onMode)
    this.#syncSliderFromStore()
    this.#syncVisibility()
  }

  disconnect() {
    editorStore.removeEventListener("sun:change", this._onSun)
    editorStore.removeEventListener("insolation:mode-change", this._onMode)
    // Guard against a dangling body class if the controller is removed while
    // insolation mode is on (HMR, future Turbo frames, tests).
    document.body.classList.remove("insolation-bar-visible")
  }

  hourChanged() {
    const hour = parseFloat(this.hourSliderTarget.value)
    if (!Number.isFinite(hour)) return
    const d = new Date(editorStore.sun.dateIso)
    const h = Math.floor(hour)
    const m = Math.round((hour - h) * 60)
    d.setHours(h, m, 0, 0)
    editorStore.setSunDateIso(isoLocal(d))
  }

  openMenu() {
    // Reuse the event the dock fires so the bottom sheet opens to the
    // insolation tab. Plain CustomEvent keeps the cross-controller coupling
    // explicit — `dispatch` with a custom prefix hides it.
    this.element.dispatchEvent(new CustomEvent("mobile-dock:open-menu", { bubbles: true }))
  }

  turnOff() {
    editorStore.setInsolationMode("off")
  }

  #syncSliderFromStore() {
    const d = new Date(editorStore.sun.dateIso)
    const hourDec = d.getHours() + d.getMinutes() / 60
    const label = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
    if (this.hasHourSliderTarget) {
      this.hourSliderTarget.value = hourDec.toFixed(2)
      // Screen readers otherwise announce the raw float ("12.08") rather
      // than "12:05" — aria-valuetext overrides the value announcement.
      this.hourSliderTarget.setAttribute("aria-valuetext", label)
    }
    if (this.hasHourLabelTarget) this.hourLabelTarget.textContent = label
  }

  #syncVisibility() {
    const on = editorStore.insolationMode !== "off"
    this.element.classList.toggle("hidden", !on)
    // Body class so the onboarding hint (and any other bottom-anchored
    // overlay) can shift up out of the bar's way via CSS. Cleaner than
    // tight coupling between controllers.
    document.body.classList.toggle("insolation-bar-visible", on)
  }
}
